/**
 * Analytics read + its serializer.
 *
 * The serializer enforces `local_date XOR (start_time + end_time)`. Sending both
 * (or neither) is a backend validation error, and the union in the filter state
 * plus this switch make the invalid combinations unrepresentable rather than
 * merely unlikely.
 */
import type { AxiosResponse } from "axios";
import axiosClient from "@/shared/lib/axios/axios.ts";
import { API_ROUTES } from "@/alerts/api/config.ts";
import {
  unwrapEnvelope,
  toAlertApiError,
  type ApiEnvelope,
} from "@/alerts/api/envelope.ts";
import { isAlertsMockMode } from "@/alerts/api/mocks/alerts.mocks.ts";
import { mockAlertAnalytics } from "@/alerts/api/mocks/alerts-analytics.mocks.ts";
import type {
  AlertAnalyticsFilterState,
  AlertAnalyticsResponse,
} from "@/alerts/types/alerts-analytics.ts";

const csv = (values?: Array<string | number>): string | undefined =>
  values && values.length > 0 ? values.join(",") : undefined;

/** Serialize scope + exactly one time mode. */
export const buildAlertAnalyticsParams = (
  state: AlertAnalyticsFilterState,
): Record<string, unknown> => {
  const { scope, period } = state;
  const params: Record<string, unknown> = {
    location_id: scope.location_id,
    machine_ids: csv(scope.machine_ids),
    device_mxids: csv(scope.device_mxids),
    type_in: csv(scope.type_in),
  };

  // Exactly one mode reaches the wire. A day is a LOCAL date resolved in the
  // location's timezone by the backend — never a UTC range we compute here.
  if (period.mode === "day") {
    params.local_date = period.local_date;
  } else {
    params.start_time = period.start_time;
    params.end_time = period.end_time;
  }

  Object.keys(params).forEach((key) => params[key] === undefined && delete params[key]);
  return params;
};

/** GET /api/alerts/analytics/ — workload and outcomes for a scope + period. */
export const getAlertAnalytics = async (
  state: AlertAnalyticsFilterState,
  signal?: AbortSignal,
): Promise<AlertAnalyticsResponse> => {
  if (isAlertsMockMode()) return mockAlertAnalytics(state);
  try {
    const response = await axiosClient.get<
      unknown,
      AxiosResponse<ApiEnvelope<AlertAnalyticsResponse, unknown>>
    >(API_ROUTES.ALERTS_ANALYTICS, {
      params: buildAlertAnalyticsParams(state),
      signal,
    });
    return unwrapEnvelope(response).data;
  } catch (error) {
    throw toAlertApiError(error);
  }
};
